import { defineStore } from 'pinia'
import { useThemeStore } from '@js/store/ThemeStore.js'
import { useCartStore } from '@js/store/CartStore.js'
import { checkDocument } from '@js/helpers.ts'

const formDefaultData = {
    product: null,
    characteristics_grouped: null,
    bitrix_title: 'Быстрый заказ',
    data: {
        full_name: '',
        phone: '',
        captcha_token: '',
    },
    isOpen: false,
    errors: [],
    animation_in_progress: false,
    sending: false,
    successfully_sent: false,
}

export const useProductStore = defineStore('productStore', {
    state: () => ({
        product: null,
        breadcrumbs: null,

        // попап «быстрый заказ»
        callback: { ...formDefaultData },
    }),

    actions: {
        resetState() {},

        setBetonData(payload) {
            const specs = Array.isArray(payload) ? payload : (payload?.specifications ?? [])
        },

        // получить/сохранить выбор ТОЛЬКО для текущего товара
        getSelection() {
            const cart = useCartStore()
            const id = this.product?.id
            if (!id) return { type: null, price_type: null, fiber: null }
            return cart.getSelection(id)
        },

        setSelection(part /* {type?, price_type?, fiber?} */) {
            if (!this.product?.id) return
            const cart = useCartStore()
            const prev = cart.getSelection(this.product.id)
            const next = { ...prev, ...part }
            cart.setSelection(this.product.id, next) // сохраняем выбор (cookie + память)
            this.applyPrice()                        // сразу пересчитываем и подменяем цену
        },

        _computePriceBySelection(product, specs, sel) {
            const base = Number(product?.price || 0)
            if (!Array.isArray(specs) || !specs.length) return base

            const byType = specs.filter(s => !sel.type || s.type === sel.type)
            const byFiber = byType.filter(s => {
                if (!sel.fiber) return true
                return Number(s[sel.fiber]) === 1
            })
            const list = byFiber.length ? byFiber : byType
            if (!list.length) return base

            const field = sel.price_type ?? 'price'
            const nums = list
                .map(s => Number(String(s[field] ?? 0)))
                .filter(n => Number.isFinite(n) && n > 0)

            if (!nums.length) return base
            return Math.min(...nums)
        },

        applyPrice() {
            if (!this.product) return
            const sel = this.getSelection()
            const next = this._computePriceBySelection(
                this.product,
                sel
            )
            this.product.current_price = Number(next)
            this.product.price = Number(next) // чтобы все компоненты читали актуальную цену
            try {
                const cart = useCartStore()
                cart.updateCatalogPrice?.(this.product.id, Number(next)) // синхроним с корзиной/итогами
            } catch (_) {}
        },

        // ===== Попап «Быстрый заказ» (как у тебя было) =====
        sendPopupForm() {
            if (!checkDocument()) return

            this.callback.errors = []
            this.callback.sending = false
            this.callback.data.captcha_token = ''
            this.callback.successfully_sent = false

            const captchaId = 'smart-captcha-container-base-callback'

            // 1) снести старый контейнер/виджет
            const existingContainer = document.getElementById(captchaId)
            if (existingContainer) {
                try {
                    window.smartCaptcha.destroy(existingContainer.dataset.widgetId)
                } catch (e) {}
                existingContainer.remove()
            }

            // 2) создать новый контейнер
            const container = document.createElement('div')
            container.id = captchaId
            container.style.position = 'absolute'
            container.style.zIndex = '-1'
            document.body.appendChild(container)

            let tokenReceived = false

            // 3) рендер капчи
            const widgetId = window.smartCaptcha.render(captchaId, {
                sitekey: import.meta.env.VITE_YANDEX_SMARTCAPTCHA_SECRET,
                invisible: true,
                hideShield: true,
                callback: (token) => {
                    tokenReceived = true
                    this.callback.sending = true
                    this.callback.data.captcha_token = token

                    // передаём текущий продукт (с уже подменённой ценой)
                    axios.post('/axios/form/send-lead', {
                        bitrix_title: this.callback.bitrix_title,
                        ...this.callback.data,
                        cart_products: [this.callback.product],
                    }).then(() => {
                        this.callback.sending = false
                        this.callback.successfully_sent = true
                        this.callback.data.captcha_token = ''

                        try { window.smartCaptcha.destroy(widgetId) } catch (e) {}
                    }).catch(error => {
                        this.callback.sending = false
                        if (error.response?.status === 422) {
                            this.callback.errors = error.response.data.errors
                        }
                    })
                }
            })

            // связать id
            document.getElementById(captchaId).dataset.widgetId = widgetId

            // 4) закрытие пользователем
            window.smartCaptcha.subscribe(widgetId, 'challenge-hidden', () => {
                if (!tokenReceived) {
                    this.callback.sending = false
                    this.callback.errors = {
                        captcha_token: ['Вы закрыли капчу. Повторите попытку.']
                    }
                    try { window.smartCaptcha.reset(widgetId) } catch (e) {}
                }
            })

            // 5) запуск
            setTimeout(() => {
                try { window.smartCaptcha.execute(widgetId) }
                catch (e) {
                    this.callback.sending = false
                    this.callback.errors = {
                        captcha_token: ['Не удалось запустить капчу. Попробуйте ещё раз.']
                    }
                }
            }, 100)
        },

        showFastBuyPopup(id) {
            console.log(1,this.callback.isOpen)

            axios.post('/axios/products/get-by-id', { id })
                .then(response => {
                    this.callback.product = response.data

                    this.callback.animation_in_progress = false
                    this.callback.isOpen = true
                    this.callback.successfully_sent = false

                    setTimeout(() => {
                        this.callback.animation_in_progress = true
                    }, 50)
                })
        },

        closeFastBuyPopup() {
            Object.assign(this.callback, structuredClone(formDefaultData))
        }
    },
})
